import React from 'react';
import pluralize from 'pluralize';
import { Stage } from '../Home';
import { STAGE_ACTION_CONFIG } from '../progressConfig';
import { addFilterToLink } from '../utils';
import { StatisticValue, StatisticsStatus } from '../stageStatistics';
import * as styles from './StageProgressSummary.module.scss';
import { Button, Heading, Icon, IconList, Strong, Text } from 'components/core';
import { Inline, Stack } from 'components/layout';

type ProgressCardProps = {
  count: number;
  text: string;
  path: string;
  label: string;
  isPrimaryAction?: boolean;
};

const ProgressCard = ({
  count,
  text,
  path,
  label,
  isPrimaryAction = false,
}: ProgressCardProps) => {
  return (
    <Stack spacing={3}>
      <Text size="md">{text}</Text>
      <Heading type="h2" component="p" className={styles.counter}>
        {count}
      </Heading>
      <Button
        to={path}
        size="md"
        type={isPrimaryAction ? 'brand' : 'neutral'}
        variant={isPrimaryAction ? 'solid' : 'outline'}
        aria-label={label}
      >
        Continue
      </Button>
    </Stack>
  );
};

type CreateTemplateEntryPointProps = {
  reviewerName: string;
  availableStudyCount: number;
  createTemplateUrl: string;
  studyListUrl: string;
};

const CreateTemplateEntryPoint = ({
  reviewerName,
  availableStudyCount,
  createTemplateUrl,
  studyListUrl,
}: CreateTemplateEntryPointProps) => {
  return (
    <Stack alignX="center" spacing={4}>
      <Heading type="h4" component="p">
        {reviewerName}, you have
      </Heading>
      <Heading type="h2" component="p">
        {availableStudyCount}
      </Heading>
      <Text size="md">included {pluralize('study', availableStudyCount)}</Text>
      <Inline spacing={3}>
        <Button to={createTemplateUrl} size="md" type="brand" variant="solid">
          Create template
        </Button>
        <Button to={studyListUrl} size="md" type="neutral" variant="outline">
          View studies
        </Button>
      </Inline>
    </Stack>
  );
};

type ActiveStageOverviewProps = Omit<
  StageProgressSummaryProps,
  'isStageComplete' | 'isStageNotStarted'
>;

const ActiveStageOverview = ({
  reviewerName,
  stage,
  availableStudyCount = 0,
  pendingConsensusCount = 0,
  studyListUrl,
  contributionCount,
  contributionStatus,
  contributionRetry,
  chooseYourTemplateUrl,
}: ActiveStageOverviewProps) => {
  const {
    primaryAction,
    pastTense,
    primaryActionLabel,
    consensusActionLabel,
    consensusAction,
    consensusFilter,
  } = STAGE_ACTION_CONFIG[stage];
  const hasAvailableStudies = availableStudyCount > 0;
  const hasPendingConsensus = pendingConsensusCount > 0;

  if (chooseYourTemplateUrl) {
    return (
      <CreateTemplateEntryPoint
        reviewerName={reviewerName}
        availableStudyCount={availableStudyCount}
        createTemplateUrl={chooseYourTemplateUrl}
        studyListUrl={studyListUrl}
      />
    );
  }

  return (
    <Stack alignX="center" spacing={4}>
      <Heading type="h4" component="p">
        {reviewerName}, you can still
      </Heading>
      <Inline spacing={8}>
        {hasPendingConsensus && (
          <ProgressCard
            text={consensusAction}
            path={addFilterToLink(studyListUrl, consensusFilter)}
            count={pendingConsensusCount}
            label={consensusActionLabel}
          />
        )}
        {hasAvailableStudies && (
          <ProgressCard
            isPrimaryAction
            count={availableStudyCount}
            text={primaryAction}
            path={studyListUrl}
            label={primaryActionLabel}
          />
        )}
      </Inline>
      <Inline spacing={1}>
        <Icon icon={IconList.solid.faChartSimple} variant="success" />
        <Text>
          You&apos;ve {pastTense}{' '}
          <strong>
            <StatisticValue
              status={contributionStatus}
              value={contributionCount}
            />
          </strong>{' '}
          {pluralize(
            'study',
            contributionStatus === 'success' ? contributionCount : 2
          )}{' '}
          so far
        </Text>
        {contributionStatus === 'error' && contributionRetry}
      </Inline>
    </Stack>
  );
};

type StageProgressSummaryProps = {
  stage: Stage;
  isStageComplete: boolean;
  isStageNotStarted: boolean;
  reviewerName: string;
  contributionCount: number;
  contributionStatus: StatisticsStatus;
  contributionRetry?: React.ReactNode;
  pendingConsensusCount?: number;
  availableStudyCount?: number;
  studyListUrl: string;
  chooseYourTemplateUrl?: string;
};

export const StageProgressSummary = ({
  stage,
  isStageComplete,
  isStageNotStarted,
  reviewerName,
  contributionCount,
  contributionStatus,
  contributionRetry,
  availableStudyCount,
  pendingConsensusCount,
  studyListUrl,
  chooseYourTemplateUrl,
}: StageProgressSummaryProps) => {
  const { pastTense } = STAGE_ACTION_CONFIG[stage];

  if (isStageNotStarted) {
    return (
      <Stack alignX="center">
        <Text variant="weak" size="xl">
          {reviewerName},<br /> you have not yet started
        </Text>
      </Stack>
    );
  }

  if (isStageComplete) {
    return (
      <Stack spacing={4} alignX="center">
        <Icon
          aria-label="Thumbs up"
          variant="success"
          size="xl"
          icon={IconList.solid.faThumbsUp}
        />
        <Text size="xl">
          {reviewerName},<br /> you have {pastTense}{' '}
          <Strong>
            <StatisticValue
              status={contributionStatus}
              value={contributionCount}
            />
          </Strong>{' '}
          studies
        </Text>
        {contributionStatus === 'error' && contributionRetry}
      </Stack>
    );
  }

  return (
    <ActiveStageOverview
      stage={stage}
      reviewerName={reviewerName}
      availableStudyCount={availableStudyCount}
      pendingConsensusCount={pendingConsensusCount}
      studyListUrl={studyListUrl}
      contributionCount={contributionCount}
      contributionStatus={contributionStatus}
      contributionRetry={contributionRetry}
      chooseYourTemplateUrl={chooseYourTemplateUrl}
    />
  );
};
